// Vanga Cart + Klaviyo Integration
// This script intercepts Vanga's cart add events and tracks them with Klaviyo
// IMPROVED: Works even if Vanga loads after this script

(function() {
  'use strict';

  // Keep re-wrapping fetch to ensure we're always intercepting, even if Vanga overwrites it
  let isInterceptorActive = false;
  let originalFetch = window.fetch;

  // Prevent duplicate tracking events
  const recentlyTracked = new Map();
  const DEDUP_WINDOW_MS = 2000; // 2 seconds

  function installInterceptor() {
    if (isInterceptorActive) return;

    const currentFetch = window.fetch;

    // Override fetch to intercept cart add requests
    window.fetch = function(...args) {
      const [url, options] = args;

      // Convert url to string in case it's a Request object
      const urlString = typeof url === 'string' ? url : (url && typeof url === 'object' && 'url' in url ? url.url : String(url || ''));

      // Check if this is a cart add request
      if (urlString && (urlString.includes('/cart/add') || urlString.includes('/cart/add.js'))) {

        // Call the current fetch (which might be Vanga's or original)
        return currentFetch.apply(this, args)
          .then(async response => {
            // Clone the response so we can read it
            const clonedResponse = response.clone();

            try {
              // Get the response data
              const data = await clonedResponse.json();

              // Track with Klaviyo if the add was successful
              if (data && !data.status && window._learnq) {
                // Normalize items: some responses return a single item object, others return { items: [ ... ] }
                const rawItems = Array.isArray(data.items) ? data.items : (data.item ? [data.item] : null);

                // If rawItems is null, fall back to using top-level data as a single item
                const itemsArray = rawItems && rawItems.length ? rawItems : [data];

                // Helper to pick a price value and convert cents->dollars when needed
                const normalizePrice = (it) => {
                  const maybe = it.final_price ?? it.price ?? it.price_cents ?? it.variant_price ?? 0;
                  // If price looks like cents (integer >= 1000) assume cents
                  if (typeof maybe === 'number' && Math.abs(maybe) >= 1000) return maybe / 100;
                  // If it's clearly in dollars already or small, return as-is
                  return maybe;
                };

                // Normalize each item into Klaviyo expected shape
                // Use JSON parse/stringify to create clean objects without prototype pollution
                const mappedItems = itemsArray.map((it) => {
                  const price = normalizePrice(it) || 0;
                  const qty = it.quantity ?? it.qty ?? it.quantity_added ?? 1;
                  const title = it.product_title || it.title || it.name || '';
                  const url = it.url || it.product_url || it.handle ? (window.location.origin + (it.url || ('/' + it.handle))) : window.location.origin;
                  const categories = it.product_type ? [it.product_type] : [];

                  // Create clean object
                  const item = {};
                  item.ProductID = String(it.product_id ?? it.id ?? it.variant_id ?? '');
                  item.SKU = String(it.sku ?? it.variant_sku ?? '');
                  item.ProductName = String(title);
                  item.Quantity = Number(qty);
                  item.ItemPrice = Number(price);
                  item.RowTotal = Number(price * qty);
                  item.ProductURL = String(url);
                  item.ImageURL = String(it.featured_image?.url || it.image || '');
                  if (categories.length > 0) {
                    item.ProductCategories = categories.map(c => String(c));
                  }

                  return item;
                });

                // Compute totals
                const totalValue = mappedItems.reduce((sum, i) => sum + (Number(i.RowTotal) || 0), 0);

                const first = mappedItems[0] || {};

                // Create clean tracking data object using Klaviyo's standard format
                const trackingData = {};
                trackingData.$value = Number(totalValue);

                // Single item properties (for the main item added)
                trackingData.ProductName = String(first.ProductName || '');
                trackingData.ProductID = String(first.ProductID || '');
                trackingData.SKU = String(first.SKU || '');
                trackingData.ImageURL = String(first.ImageURL || '');
                trackingData.URL = String(first.ProductURL || '');
                trackingData.Price = Number(first.ItemPrice || 0);
                trackingData.Quantity = Number(first.Quantity || 0);

                // Also include "Added" prefixed versions for compatibility
                trackingData.AddedItemProductName = String(first.ProductName || '');
                trackingData.AddedItemProductID = String(first.ProductID || '');
                trackingData.AddedItemSKU = String(first.SKU || '');
                trackingData.AddedItemImageURL = String(first.ImageURL || '');
                trackingData.AddedItemURL = String(first.ProductURL || '');
                trackingData.AddedItemPrice = Number(first.ItemPrice || 0);
                trackingData.AddedItemQuantity = Number(first.Quantity || 0);

                // Multiple items array
                trackingData.ItemNames = mappedItems.map(i => String(i.ProductName)).filter(n => n && n !== 'undefined');
                trackingData.CheckoutURL = String(window.location.origin + '/cart');
                trackingData.Items = mappedItems;

                // Add categories if available
                if (first.ProductCategories && first.ProductCategories.length > 0) {
                  trackingData.Categories = first.ProductCategories;
                }

                // Create a unique key for this event to prevent duplicates
                const eventKey = `${first.ProductID}-${first.Quantity}-${totalValue}`;
                const now = Date.now();

                // Check if we recently tracked this exact event
                if (recentlyTracked.has(eventKey)) {
                  const lastTracked = recentlyTracked.get(eventKey);
                  if (now - lastTracked < DEDUP_WINDOW_MS) {
                    return response;
                  }
                }

                // Update the tracking timestamp
                recentlyTracked.set(eventKey, now);

                // Clean up old entries to prevent memory leak
                for (const [key, timestamp] of recentlyTracked.entries()) {
                  if (now - timestamp > DEDUP_WINDOW_MS) {
                    recentlyTracked.delete(key);
                  }
                }

                // Push to Klaviyo using _learnq queue
                try {
                  if (window._learnq && typeof window._learnq.push === 'function') {
                    window._learnq.push(['track', 'Added to Cart', trackingData]);
                  }
                } catch (error) {
                  // Silently handle Klaviyo tracking errors
                }
              }
            } catch (error) {
              // Silently handle response processing errors
            }

            // Return the original response
            return response;
          })
          .catch(error => {
            throw error;
          });
      }

      // For non-cart requests, just use current fetch
      return currentFetch.apply(this, args);
    };

    isInterceptorActive = true;
  }

  // Install immediately
  installInterceptor();

  // Re-install after Vanga might have loaded (check multiple times)
  setTimeout(() => {
    isInterceptorActive = false;
    installInterceptor();
  }, 1000);

  setTimeout(() => {
    isInterceptorActive = false;
    installInterceptor();
  }, 2000);

  // Also try after window load
  window.addEventListener('load', () => {
    setTimeout(() => {
      isInterceptorActive = false;
      installInterceptor();
    }, 500);
  });


})();
